/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useContext } from 'react';
import { AppContext } from 'Context';
import { dataFetcher, endpoints } from 'Api';
import {
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
  Button,
  TextField,
  withStyles
} from '@material-ui/core';
import {
  Close,
  Add,
  Category
} from '@material-ui/icons';

const BlueTextField = withStyles({
  root: {
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: '#5aa5ef',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#5aa5ef',
      },
    },
  },
})(TextField);

const AddSubCategoryModal = ({
  open = false,
  onClose,
  onSubCategoryCreated
}) => {
  const { setAppSnackbar, langText } = useContext(AppContext);
  const layoutText = langText?.layout;
  const commonText = langText?.common;
  const [subCategoryName, setSubCategoryName] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const handleClose = () => {
    setSubCategoryName('');
    setIsAdding(false);
    if (onClose) {
      onClose();
    }
  };

  const handleAddSubCategory = async () => {
    if (!subCategoryName?.trim()) return;

    setIsAdding(true);
    try {
      const response = await dataFetcher(endpoints.addUpdateMySubCategory, {
        title: subCategoryName.trim()
      });

      if (response.success) {
        setAppSnackbar({
          isVisible: true,
          message: response.message || `Subcategory '${subCategoryName}' was created`,
        });

        // Call the callback with the new subcategory info
        if (onSubCategoryCreated) {
          onSubCategoryCreated({
            id: response.subcategory_id || response.id,
            title: subCategoryName.trim()
          });
        }

        handleClose();
      } else {
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: response.message || 'Oops! Something went wrong while creating your subcategory'
        });
      }
    } catch (error) {
      const responseData = error?.response?.data?.message;
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: responseData || 'Oops! Something went wrong while creating your subcategory'
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && subCategoryName?.trim() && !isAdding) {
      handleAddSubCategory();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 16,
          maxWidth: 450,
        }
      }}
    >
      <DialogContent style={{ padding: 0 }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          backgroundColor: '#F8F9FA',
          borderRadius: '16px 16px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div
              style={{
                backgroundColor: '#f3f4f6',
                borderRadius: '10px',
                padding: '8px',
                display: 'flex',
                marginRight: 12,
              }}
            >
              <Category style={{ color: '#5aa5ef', fontSize: 24 }} />
            </div>
            <h2 style={{
              margin: 0,
              color: '#1A1A1A',
              fontSize: 18,
              fontWeight: 600,
              fontFamily: 'Montserrat, sans-serif'
            }}
            >
              {layoutText?.addNewSubCategory || 'Add New Subcategory'}
            </h2>
          </div>
          <IconButton
            onClick={handleClose}
            style={{ color: '#6B7280' }}
          >
            <Close />
          </IconButton>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          <label
            style={{
              display: 'block',
              marginBottom: 8,
              fontSize: 13,
              fontWeight: 600,
              color: '#1f2937',
              fontFamily: 'Montserrat, sans-serif',
            }}
          >
            {layoutText?.subCategoryName || 'Subcategory Name'}
          </label>
          <BlueTextField
            value={subCategoryName}
            onChange={(e) => setSubCategoryName(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={layoutText?.enterSubCategoryName || 'Enter subcategory name'}
            variant="outlined"
            fullWidth
            autoFocus
            style={{
              marginBottom: 8,
            }}
            InputProps={{
              style: {
                backgroundColor: '#F9FAFB',
                borderRadius: 10,
                fontFamily: 'Montserrat, sans-serif',
                fontSize: 14,
              },
              inputProps: { maxLength: 70 }
            }}
          />
          <div style={{ textAlign: 'right', marginBottom: 16 }}>
            <span style={{
              color: (subCategoryName || '').length >= 70 ? '#EF4444' : '#9CA3AF',
              fontSize: 12,
              fontFamily: 'Montserrat, sans-serif'
            }}
            >
              {(subCategoryName || '').length}
              /70
            </span>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px 24px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 12
        }}
        >
          <Button
            onClick={handleClose}
            disabled={isAdding}
            style={{
              padding: '10px 20px',
              color: '#6B7280',
              fontSize: 14,
              fontFamily: 'Montserrat, sans-serif',
              textTransform: 'none',
            }}
          >
            {commonText?.cancel || 'Cancel'}
          </Button>

          <Button
            disabled={!subCategoryName?.trim() || isAdding}
            onClick={handleAddSubCategory}
            variant="contained"
            style={{
              backgroundColor: subCategoryName?.trim() && !isAdding ? '#5aa5ef' : '#E5E7EB',
              color: 'white',
              borderRadius: 8,
              padding: '10px 20px',
              fontSize: 14,
              fontWeight: 500,
              fontFamily: 'Montserrat, sans-serif',
              boxShadow: 'none',
              textTransform: 'none',
            }}
          >
            {isAdding ? (
              <CircularProgress size={20} style={{ color: 'white' }} />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Add style={{ fontSize: 18 }} />
                {commonText?.add || 'Add'}
              </div>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddSubCategoryModal;
